<template>
  <TableCell ref="tableCell" :style="cellStyle"
    :tag
    :popoverTitle
    :popoverPadding
    :selectedCells
    v-model:showingPopover="showingPopover"
    v-model:columnSelectionCode="columnSelectionCode"
  >
    <template v-if="tag.commError.value">
      Comm error
    </template>
    <template v-else-if="tag.rangeError.value">
      Range error
    </template>
    <template v-else>
      {{ convertedValueString }}
      <span v-html="unitHtml(convertedUnit)"></span>
    </template>
    
    <template #BinSelectionInput>
      <slot name="BinSelectionInput"></slot>
    </template>

    <template #PopoverContentDesktop>
      <component :is="popoverComponent" v-bind="popoverComponentProps"></component>
    </template>

  </TableCell>
</template>

<script setup>
import { computed, ref, shallowRef, watch } from "vue";
import { createAggregateAlarmRows, shouldShowCheckAlarmsWhenNotRunningSetting } from "./appUtility.js";
import { showingDesktopUi } from "./store.js";
import { convertValue, convertUnit, unitHtml, unitRoundForStatusPage } from "./units.js";
import TableCell from "./TableCell.vue";
import AggregateStatusPopoverSimple from "./AggregateStatusPopoverSimple.vue";
import AggregateStatusPopoverDetailed from "./AggregateStatusPopoverDetailed.vue";
import AggregateStatusPopoverCalibration from "./AggregateStatusPopoverCalibration.vue";
import { createSingleColumnTableData, resolveTable } from "./TableUtility";
import { sortBy } from "./utility";

let props = defineProps([
  "tag",
  "settings",
  "columnTag",
  "popoverTitle",
  "showingPopover",
  "showControlTempPriority",
  "precision",
  "columnSelectionCode",
  "selectedCells"
]);

let showingPopover = defineModel("showingPopover");
let columnSelectionCode = defineModel("columnSelectionCode");

let cellStyle = computed(() => {
  let alarmTripped = (
    props.tag.highAlarm.tripped.value ||
    props.tag.lowAlarm.tripped.value ||
    props.tag.commErrorAlarmTripped.value ||
    props.tag.rangeErrorAlarmTripped.value
  );
  let backgroundColor = alarmTripped ? "var(--red-100)" : "";
  return { backgroundColor };
});

let unit = computed(() => {
  let tag = props.tag;
  let system = tag.$session.system;
  let sensorType = system.sensorTypes.value[tag.sensorType.value];
  return sensorType.unit;
});

let convertedUnit = computed(() => {
  return convertUnit(unit.value);
});

let convertedValue = computed(() => {
  return convertValue(props.tag.$value(), unit.value);
});

let convertedValueString = computed(() => {
  return unitRoundForStatusPage(
    convertedValue.value,
    convertedUnit.value,
    props.precision ?? props.tag.$precision()
  );
});

let bin = computed(() => {
  let system = props.tag.$session.system;
  let bins = system.bins.children;
  let bin = props.tag.$parents().find(parent => bins.includes(parent));
  return bin;
});

let controlTempAggrs = computed(() => {
  if (bin.value == null)
    return [];

  let temperatureAggrs = [
    bin.value.supply.temperature,
    bin.value.returnAir.temperature,
    ... bin.value.auxiliaries.children.map(aux => aux.temperature)
  ];
  let controlTempAggrs = temperatureAggrs.filter(aggr => aggr.controlTempToggle.value);
  let controlTempAggrsByPriority = sortBy(controlTempAggrs, aggr => aggr.controlTempPriority.value);
  return controlTempAggrsByPriority;
});

let showControlTempPriority = computed(() => {
  return Boolean (
    props.showControlTempPriority &&
    bin.value.controlTemp.method.value == "prio" &&
    controlTempAggrs.value.length >= 2
  );
});

let settingsTable = computed(() => {
  let session = props.tag.$session;
  let system = session.system;
  let multiColumnData = system.tables[props.settings]?.value;

  let tableData = null;
  if (multiColumnData == null) {
    tableData = {
      tableName: "",
      columns: [{ tag: props.tag, name: "", subColumns: [] }],
      rows: []
    };
  }
  else {
    let bin = props.columnTag;
    let binNumber = bin.$key();
    tableData = (binNumber == null) ? multiColumnData : createSingleColumnTableData(multiColumnData, binNumber);
  }
    
  let table = resolveTable(session, system, tableData);
  if (system.alarmsEnabled.value) {
    table.rows.push(... createAggregateAlarmRows(props.tag, unit.value, props.selectedCells));
  }
  return table;
});

let showCheckAlarmsWhenNotRunningSetting = computed(() => {
  return shouldShowCheckAlarmsWhenNotRunningSetting(props.tag);
});

watch(showCheckAlarmsWhenNotRunningSetting, () => {
  tableCell.value?.repositionPopover();
});

let showingSettingsTable = computed(() => {
  return settingsTable.value?.rows.length > 0;
});

let initialPopoverComponent = computed(() => {
  let showingDetailedPopover = (
    showingSettingsTable.value || 
    props.tag.sensors.children.length > 1 ||
    props.tag.rangeError.value || // TODO: Show value causing range error in simple popover also. For now, shown in detailed popover only.
    !showingDesktopUi.value
  );
  if (showingDetailedPopover)
    return AggregateStatusPopoverDetailed;
  else
    return AggregateStatusPopoverSimple;
})

let popoverComponent = shallowRef(null);
watch([showingPopover, initialPopoverComponent], () => {
  if (showingPopover.value)
    popoverComponent.value = initialPopoverComponent.value;
  else
    popoverComponent.value = null;
});

let tableCell = ref();
watch(popoverComponent, async () => {
  if (showingPopover.value)
    await tableCell.value.repositionPopover();
});

let popoverComponentProps = computed(() => {
  if (popoverComponent.value == AggregateStatusPopoverSimple)
    return popoverComponentPropsSimple();
  else if (popoverComponent.value == AggregateStatusPopoverDetailed)
    return popoverComponentPropsDetailed();
  else if (popoverComponent.value == AggregateStatusPopoverCalibration)
    return popoverComponentPropsCalibration();
});

function popoverComponentPropsSimple() {
  return {
    tag: props.tag,
    popoverTitle: props.popoverTitle,
    controlTempAggrs: controlTempAggrs.value,
    showControlTempPriority: showControlTempPriority.value,
    "onUpdate:showingPopover": () => showingPopover.value = false,
    onCalibrate: () => popoverComponent.value = AggregateStatusPopoverCalibration
  };
}

function popoverComponentPropsDetailed() {
  return {
    tag: props.tag,
    columnTag: props.columnTag,
    popoverTitle: props.popoverTitle,
    settingsTable: settingsTable.value,
    controlTempAggrs: controlTempAggrs.value,
    showControlTempPriority: showControlTempPriority.value,
    "onUpdate:showingPopover": () => showingPopover.value = false,
    onCalibrate: async () => {
      popoverComponent.value = AggregateStatusPopoverCalibration;
    }
  };
}

function popoverComponentPropsCalibration() {
  return {
    sensor: props.tag.sensors.children[0],
    popoverTitle: props.popoverTitle,
    showChannel: true,
    value: convertedValue.value,
    onCancel: () => showingPopover.value = false,
    onOk: () => showingPopover.value = false
  };
}

let popoverPadding = computed(() => {
  if (popoverComponent.value == AggregateStatusPopoverSimple)
    return "0px";
  else
    return undefined;
});
</script>